import { type Hit } from 'instantsearch.js'

import { type AlgoliaRecord, type BestOffer } from '../../../algolia'
import {
  GTM_EVENT_CLICK_PRODUCT,
  GTM_EVENT_CLICK_PRODUCT_OFFERS,
} from '../../../gtm'
import { queryStringEncode } from '../../../url-handling'

import { productSearchAlgoliaAccessorGetVariantsData } from './productSearchAlgoliaAccessorGetVariantsData'

interface ProductSearchAlgoliaAccessorGetOffersData {
  bestOffer: BestOffer | null | undefined
  hasNonBestOfferSellerPromotion?: boolean
  isSaeOffer: boolean
  productHasMultipleOffers: boolean
  productUrl?: string
  query?: string
  shouldDisplayLinkToOfferPage: boolean
}

const OFFER_TYPE_SAE = 'SAE'

const productIsSaeOffer = (
  bestOffer: BestOffer | null | undefined,
  isMarketplace: boolean,
): boolean => {
  return !isMarketplace || bestOffer?.type === OFFER_TYPE_SAE
}

type ProductOrOffersUrlParams = {
  params: {
    eventName?: string
    position: number | undefined
    query: string
    queryID: string
  }
  record: AlgoliaRecord
  shouldDisplayLinkToOfferPage?: boolean
}
/*
  This function appends a query parameters that triggers an automatic opening of the PDP Offers Drawer which contains all the offers of the product.
 */
const getProductOrOffersUrl = ({
  params,
  record: { deeplink, objectID },
  shouldDisplayLinkToOfferPage,
}: ProductOrOffersUrlParams): string => {
  if (!deeplink) {
    return ''
  }

  // fix copied from https://github.com/RedTecLab/home-one/blob/8ae0a5263278555e9091d0f6d8363f80ac1f856e/app/domain/algolia/model/AlgoliaRecordAccessor.ts#L113-L115
  const deepLink = deeplink.startsWith('/') ? deeplink : `/${deeplink}`

  return `${deepLink}${queryStringEncode({
    ...params,
    eventName: decodeURIComponent(
      shouldDisplayLinkToOfferPage
        ? GTM_EVENT_CLICK_PRODUCT_OFFERS
        : GTM_EVENT_CLICK_PRODUCT,
    ),
    eventType: 'click',
    objectIDs: `[${objectID}]`,
    // if the product has offers then we return the product page url that will access the PDP with the right-side drawer opened
    ...(shouldDisplayLinkToOfferPage && {
      offerSelectionDrawer: true,
    }),
  })}`
}

/*
  This accessor is responsible for maintaining all the logic to determine if we have bestOffers, if the offer is an SAE type and so on.
  Any additional logic related to the offers should be within this accessor or coming into it via imports.
  If we need more conditional logic on the UI then we will use export flags from the accessor and avoid too much logic on the actual offer data in the UI.
 */
export const productSearchAlgoliaAccessorGetOffersData = (
  algoliaRecord: Hit<AlgoliaRecord>,
  isMarketplace: boolean,
  query = '',
): ProductSearchAlgoliaAccessorGetOffersData => {
  const {
    __position,
    __queryID,
    best_offer: bestOffer,
    hasNonBestOfferSellerPromotion,
    offers_count: offersCount,
  } = algoliaRecord

  const productHasMultipleOffers = offersCount > 1
  const isSaeOffer = productIsSaeOffer(bestOffer, isMarketplace)

  const { productHasMultipleVariants } =
    productSearchAlgoliaAccessorGetVariantsData(algoliaRecord)

  const shouldDisplayLinkToOfferPage =
    productHasMultipleOffers && isMarketplace && !productHasMultipleVariants

  return {
    bestOffer,
    hasNonBestOfferSellerPromotion,
    isSaeOffer,
    productHasMultipleOffers,
    productUrl: getProductOrOffersUrl({
      params: {
        position: __position,
        query,
        queryID: __queryID ?? '',
      },
      record: algoliaRecord,
      shouldDisplayLinkToOfferPage,
    }),
    shouldDisplayLinkToOfferPage,
  }
}
